import { IGestShellMenuOption, IGestShellUser, UserData_model } from "@shared/library";
import { languages } from "@shared/library/dist/static/language";
import importer from "../helpers/importer";
import { LangKey } from "../models/ui_data/Lang_model";
import { MenuArea_model } from "../models/ui_data/MenuArea_model";
import { MenuOption_model } from "../models/ui_data/MenuOption_model";
import { ADMIN_PANEL_PATH } from "./constants";

type TLang = ReturnType<typeof languages>;

//no "protect" class on these images: the global `*:has(> img.protect)` zeroes the padding of the row that contains them
//the backend gives the name of the icon, not always: the route of the shell can give it (an element) and without any the entry has none
const renderIcon = (icon?: string) => icon ? <img src={importer.ic.require(icon)} alt="" loading="lazy" /> : undefined;

const translate = (lang: TLang, label: string) => lang[label as LangKey] ?? label;

//a disabled option stays in the sidebar (dimmed) but has no route
const toLeaf = (option: MenuOption_model, lang: TLang): IGestShellMenuOption => ({
    label: translate(lang, option.label),
    path: option.path,
    icon: renderIcon(option.icon),
    disabled: !option.enabled,
});

/** An area with a single option is that option; with more it is a group that opens its options as a tree (disabled when none of them is enabled) */
const toArea = (area: MenuArea_model, lang: TLang): IGestShellMenuOption => {
    const children = area.subs.map(option => toLeaf(option, lang));
    const node: IGestShellMenuOption = children.length === 1
        ? { ...children[0], label: translate(lang, area.label), icon: renderIcon(area.icon) ?? children[0].icon }
        : { label: translate(lang, area.label), path: `/area-${area.id}`, icon: renderIcon(area.icon), disabled: children.every(c => c.disabled), children };

    return node;
};

/**
 * Menu of the GestShell: every area of the backend with its options (disabled ones dimmed, without route), plus the administration panel for the admins.
 * The pages and the icons that can be imported are the routes of the shell (see static/routes).
 */
export const buildMenu = (areas: MenuArea_model[], lang: TLang, isAdmin: boolean) => {
    const menu = areas.filter(area => area.subs.length).map(area => toArea(area, lang));
    const admin: IGestShellMenuOption[] = isAdmin ? [{ label: "Administration Panel", path: ADMIN_PANEL_PATH }] : [];

    return [...menu, ...admin];
};

/** The user of the shell from the data of the token: the shell only knows its own fixed fields */
export const toShellUser = (userData: UserData_model): IGestShellUser => ({
    username: userData.username,
    name: userData.firstname,
    surname: userData.lastname,
    email: userData.email,
    company: userData.business,
    role: userData.role,
    language: userData.lang,
});
